import { trackCotacaoStepView } from "@/analytics/tracking";
import { useQuoteContext } from "@/contexts/quote-context";
import { Loader2, ShieldAlert, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface SuccessPageProps {
  onBack: () => void;
}

const CCS = {
  ABERTO: 1,
  EM_ATENDIMENTO: 2,
  APROVADA: 3,
  CONFIRMADA: 4,
  ENCERRADA: 6,
  CANCELADA: 5,
} as const;

type StatusUI =
  | "loading"
  | "pendente"
  | "pago"
  | "erro"
  | "cancelado"
  | "encerrado"
  | "confirmado";

export function StatusPage({ onBack }: SuccessPageProps) {
  void onBack;
  const { transactionId, checkPaymentStatus, cotacao, transaction } =
    useQuoteContext();
  const [status, setStatus] = useState<StatusUI>("loading");

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    trackCotacaoStepView("status");
  }, []);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    const ccs_codigo = cotacao?.ccs_codigo ? parseInt(cotacao.ccs_codigo) : 0;

    if (ccs_codigo === CCS.APROVADA || ccs_codigo === CCS.CONFIRMADA) {
      setStatus("confirmado");
      return;
    }

    if (ccs_codigo === CCS.CANCELADA) {
      setStatus("cancelado");
      return;
    }

    if (ccs_codigo === CCS.ENCERRADA) {
      setStatus("encerrado");
      return;
    }

    const paidInvoice = transaction?.find(
      (f) => f.tts_descricao?.toLowerCase() === "pago",
    );

    if (paidInvoice && !transactionId) {
      setStatus("pago");
      return;
    }

    const fetchStatus = async () => {
      if (!transactionId) {
        if (ccs_codigo === CCS.APROVADA || ccs_codigo === CCS.CONFIRMADA) {
          setStatus("pago");
        } else {
          setStatus("loading");
        }
        return;
      }

      try {
        const result = await checkPaymentStatus(transactionId);

        if (result === "approved") {
          setStatus("pago");
          if (pollRef.current) clearInterval(pollRef.current);
        } else {
          setStatus("pendente");
        }
      } catch {
        setStatus("encerrado");
        if (pollRef.current) clearInterval(pollRef.current);
      }
    };

    fetchStatus();

    pollRef.current = setInterval(() => {
      if (["pago", "cancelado", "encerrado", "confirmado"].includes(status)) {
        if (pollRef.current) clearInterval(pollRef.current);
        return;
      }
      fetchStatus();
    }, 30000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [
    transactionId,
    cotacao?.ccs_codigo,
    transaction,
    checkPaymentStatus,
    status,
  ]);

  const renderStatus = () => {
    if (status === "loading") {
      return (
        <div className="flex flex-col items-start h-[40vh] justify-center">
          <h1 className="text-4xl font-bold text-n-primary mb-20 text-left">
            Verificando seu pedido...
          </h1>
          <div className="flex flex-col items-center w-full">
            <Loader2 className="w-10 h-10 animate-spin text-n-primary" />
            <p className="mt-2 text-n-text-neutral">Verificando pagamento...</p>
          </div>
        </div>
      );
    }

    if (status === "confirmado") {
      return (
        <div>
          <h1 className="md:text-5xl text-4xl font-bold text-n-primary mb-4">
            Transporte confirmado
          </h1>

          <p className="md:text-lg text-n-text-neutral mb-8">
            Seu transporte foi confirmado com sucesso! Todas as informações
            detalhadas estão disponíveis na área do cliente.
          </p>

          <div className="flex flex-col bg-white rounded-xl py-6 md:py-8 px-4 md:px-6 mb-6 md:mb-8 gap-4">
            <h2 className="text-xl md:text-2xl font-semibold text-n-primary">
              Detalhes do pedido
            </h2>

            <p className="text-n-text-neutral text-sm md:text-base font-bold">
              O seu código de pedido é:{" "}
              <span className="font-bold text-n-green">
                #{cotacao.ctc_codigo}
              </span>
            </p>

            <p className="text-sm md:text-base text-n-text-neutral mb-4 md:mb-6">
              Confira todas as informações do seu transporte, datas, locais e
              acompanhe o status em tempo real através da{" "}
              <Link
                to="/cliente"
                className="text-n-primary underline hover:text-n-primary/80"
              >
                área do cliente
              </Link>
              .
            </p>

            <p className="text-sm md:text-base text-n-text-neutral">
              Precisa de um novo transporte?{" "}
              <Link
                to="/fazer-cotacao"
                className="text-n-primary underline hover:text-n-primary/80"
              >
                Fazer nova cotação
              </Link>
              .
            </p>
          </div>
        </div>
      );
    }

    if (status === "pago") {
      return (
        <div>
          <h1 className="md:text-5xl text-4xl font-bold text-n-primary mb-4">
            Parabéns
          </h1>

          <p className="md:text-lg text-n-text-neutral mb-8">
            O seu agendamento de transporte foi realizado com sucesso!
          </p>

          <div className="flex flex-col bg-white rounded-xl py-6 md:py-8 px-4 md:px-6 mb-6 md:mb-8 gap-4">
            <h2 className="text-xl md:text-2xl font-semibold text-n-primary">
              Atenção, importante!
            </h2>

            <p className="text-n-text-neutral text-sm md:text-base font-bold">
              O seu código de pedido é:{" "}
              <span className="font-bold text-n-green">
                #{cotacao.ctc_codigo}
              </span>
            </p>

            <p className="text-sm md:text-base text-n-text-neutral">
              Confira o seu email (caixa de entrada e spam) para verificar as
              próximas etapas do seu serviço e onboarding.
            </p>
            <p className="text-sm md:text-base text-n-text-neutral mt-3">
              Acompanhe o status do seu pedido pela{" "}
              <Link
                to="/cliente"
                className="text-n-primary underline hover:text-n-primary/80"
              >
                área do cliente.
              </Link>
            </p>
          </div>
        </div>
      );
    }

    if (status === "pendente") {
      return (
        <div>
          <h1 className="md:text-5xl text-4xl font-bold text-n-primary mb-4">
            Aguardando pagamento
          </h1>

          <p className="md:text-lg text-n-text-neutral mb-8">
            O seu pagamento ainda está pendente. Assim que identificarmos seu
            pagamento, seu transporte será agendado.
          </p>

          <div className="flex flex-col bg-white rounded-xl py-6 md:py-8 px-4 md:px-6 mb-6 md:mb-8 gap-4">
            <h2 className="text-xl md:text-2xl font-semibold text-n-primary">
              Atenção, importante!
            </h2>

            <p className="text-n-text-neutral text-sm md:text-base font-bold">
              O seu código de pedido é:{" "}
              <span className="font-bold text-n-green">
                #{cotacao.ctc_codigo}
              </span>
            </p>
            <div className="flex flex-col items-center mt-10">
              <Loader2 className="w-8 h-8 md:w-10 md:h-10 animate-spin" />
              <p className="mt-2 text-base md:text-lg font-semibold mb-10">
                Aguardando pagamento
              </p>
              <p className="text-xs md:text-sm text-n-text-neutral text-left">
                Assim que identificarmos seu pagamento, você receberá um e-mail
                com a confirmação.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (status === "cancelado") {
      return (
        <div>
          <h1 className="md:text-5xl text-4xl font-bold text-n-primary mb-4">
            Pedido cancelado
          </h1>

          <p className="md:text-lg text-n-text-neutral mb-8">
            Esta cotação foi cancelada e não seguirá para agendamento.
          </p>

          <div className="flex flex-col bg-white rounded-xl py-6 md:py-8 px-4 md:px-6 mb-6 md:mb-8 gap-4">
            <h2 className="text-xl md:text-2xl font-semibold text-n-primary">
              Detalhes do pedido
            </h2>

            <p className="text-n-text-neutral text-sm md:text-base font-bold">
              Código do pedido:{" "}
              <span className="font-bold text-n-red">
                #{cotacao.ctc_codigo}
              </span>
            </p>

            <div className="flex flex-col items-center my-4">
              <XCircle className="w-8 h-8 md:w-10 md:h-10 text-n-red" />
              <p className="mt-2 text-base md:text-lg font-semibold text-n-red mb-1">
                Cotação cancelada
              </p>
            </div>
            <p className="text-xs md:text-sm text-n-text-neutral text-left">
              Se precisar, você pode{" "}
              <Link
                to="/fazer-cotacao"
                className="text-n-primary underline hover:text-n-primary/80"
              >
                fazer nova cotação
              </Link>{" "}
              a qualquer momento.
            </p>
          </div>
        </div>
      );
    }

    if (status === "encerrado") {
      return (
        <div>
          <h1 className="md:text-5xl text-4xl font-bold text-n-primary mb-4">
            Cotação encerrada
          </h1>

          <p className="md:text-lg text-n-text-neutral mb-8">
            Esta cotação foi encerrada. Caso precise de um novo transporte,
            inicie uma nova solicitação.
          </p>

          <div className="flex flex-col bg-white rounded-xl py-6 md:py-8 px-4 md:px-6 mb-6 md:mb-8 gap-4">
            <h2 className="text-xl md:text-2xl font-semibold text-n-primary">
              Detalhes do pedido
            </h2>

            <p className="text-n-text-neutral text-sm md:text-base font-bold">
              Código do pedido:{" "}
              <span className="font-bold text-n-green">
                #{cotacao.ctc_codigo}
              </span>
            </p>

            <div className="flex flex-col items-center my-4">
              <ShieldAlert className="w-8 h-8 md:w-10 md:h-10 text-n-yellow" />
              <p className="mt-2 text-base md:text-lg font-semibold text-n-text mb-1">
                Cotação Encerrada
              </p>
            </div>
            <p className="text-xs md:text-sm text-n-text-neutral text-left">
              Não é possível prosseguir com esta cotação. Para continuar,{" "}
              <Link
                to="/fazer-cotacao"
                className="text-n-primary underline hover:text-n-primary/80"
              >
                fazer nova cotação
              </Link>
              .
            </p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <h1 className="md:text-5xl text-4xl font-bold text-n-primary mb-4">
          Algo deu errado!
        </h1>

        <p className="md:text-lg text-n-text-neutral mb-8">
          Ocorreu um erro ao verificar seu pagamento. Tente novamente ou entre
          em contato.
        </p>

        <div className="flex flex-col bg-white rounded-xl py-6 md:py-8 px-4 md:px-6 mb-6 md:mb-8 gap-4">
          <h2 className="text-xl md:text-2xl font-semibold text-n-primary">
            Atenção, importante!
          </h2>

          <p className="text-n-text-neutral text-sm md:text-base font-bold">
            O seu código de pedido é:{" "}
            <span className="font-bold text-n-green">
              #{cotacao.ctc_codigo}
            </span>
          </p>

          <div className="flex flex-col items-center my-4">
            <XCircle className="w-8 h-8 md:w-10 md:h-10 text-n-red" />
            <p className="mt-2 text-base md:text-lg font-semibold text-n-red mb-1">
              Erro ao verificar pagamento
            </p>
          </div>
          <p className="text-xs md:text-sm text-n-text-neutral text-left">
            Não conseguimos encontrar seu pagamento. Tente novamente ou entre em
            contato. Se precisar, você pode{" "}
            <Link
              to="/fazer-cotacao"
              className="text-n-primary underline hover:text-n-primary/80"
            >
              fazer nova cotação
            </Link>
            .
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen md:min-h-[calc(100vh-120px)] ">
      <div className="container px-2 md:px-8">
        <div className="md:py-16 py-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 md:items-center">
          <div className="order-2 md:order-1">
            {renderStatus()}

            <div className="space-y-4 md:space-y-6 mt-8">
              <div>
                <h3 className="text-lg md:text-[20px] font-semibold text-n-primary mb-2">
                  Não sabe o dia exato do seu transporte?
                </h3>
                <p className="text-n-text-neutral text-sm md:text-base ">
                  Não se preocupe, você pode alterar a qualquer momento a data.
                  Garanta as condições de seu transporte, com garantia de
                  reembolso integral.
                </p>
              </div>
              <div>
                <h3 className="text-lg md:text-[20px] font-semibold text-n-primary mb-2">
                  Dúvidas quanto ao seu pedido?
                </h3>
                <p className="text-n-text-neutral text-sm md:text-base">
                  Ligue para <span className="font-bold">0800 878 0708</span> ou
                  acesse nossa{" "}
                  <Link
                    to="/cliente"
                    className="text-n-primary underline hover:text-n-primary/80 font-semibold"
                  >
                    área do cliente
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center order-1 md:order-2 mb-4 md:mb-0">
            <img
              src="/images/rating_people.png"
              alt="Sucesso na contratação"
              className="object-contain max-h-[250px] md:max-h-[400px] w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
